import { useState, useEffect } from 'react'

// Hook
function useStorage(key, initialValue, storage = 'local') {
  const IS_SESSION = storage === 'session'

  let STORAGE_TYPE

  if (IS_SESSION) STORAGE_TYPE = window.sessionStorage
  else STORAGE_TYPE = window.localStorage
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      // Get from local storage by key
      const item = STORAGE_TYPE.getItem(key)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      return initialValue
    }
  })

  // Listen for storage changes (cross-tab and same-tab)
  useEffect(() => {
    const handleStorage = (event) => {
      if (
        (event.key && event.key === key) ||
        (event.detail && event.detail.key === key)
      ) {
        try {
          const item = STORAGE_TYPE.getItem(key)
          setStoredValue(item ? JSON.parse(item) : initialValue)
        } catch {
          setStoredValue(initialValue)
        }
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('storageChange', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('storageChange', handleStorage)
    }
  }, [key, initialValue, STORAGE_TYPE])

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      // Save to local storage
      if (typeof window !== 'undefined') {
        STORAGE_TYPE.setItem(key, JSON.stringify(valueToStore))
        // Dispatch custom event for same-tab updates
        window.dispatchEvent(
          new CustomEvent('storageChange', { detail: { key } })
        )
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error)
    }
  }

  return [storedValue, setValue]
}

export default useStorage
